import React from 'react';
import { useIsFocused } from '@react-navigation/native';
import { theme } from '@garnish/constants';

import { LoadingAnimation } from '@order/components';
import {
  TierName,
  useFetchPlansByTierIdQuery,
  useOptInUserMutation,
} from '@order/graphql';
import { useSweetpassTier } from '@order/hooks';
import { useTelemetry, useTrackEventEffect } from '@order/Telemetry';

import { SweetpassConfirmationDialog } from '../../components/SweetpassConfirmationDialog';
import type { SweetpassUpgradeScreenProps } from '../../navigation/AppNavigation.props';
import { getSweetpassUpgradeScreenPaletteForTag } from './SweetgreenUpgradeScreenPalette';
import type {
  SweetpassUpgradeCtaHandler,
  SweetpassUpgradePageTag,
} from './SweetpassUpgradeScreen.loaded';
import { useUpgradeScreenLoadedContent } from './SweetpassUpgradeScreen.loaded';
import { SweetpassUpgradeScreenContent } from './SweetpassUpgradeScreenContent';

// ────────────────────────────────────────────────────────────────────────────────

export const SweetpassUpgradeScreen = ({
  navigation,
}: SweetpassUpgradeScreenProps) => {
  const {
    isSweetpassEnabled,
    isSweetpassTierLoading,
    sweetpassNextTierId,
    sweetpassNextTierName,
  } = useSweetpassTier('cache-and-network');
  const { track } = useTelemetry();

  const [showConfirmationModal, setShowConfirmationModal] =
    React.useState(false);

  const nextTierId = sweetpassNextTierId ?? '';
  const [plansByTierId] = useFetchPlansByTierIdQuery({
    variables: { tierId: nextTierId },
    pause: !nextTierId,
  });

  const [optInMutationResponse, optInMutation] = useOptInUserMutation();
  const isLoadingOptInFreeTier =
    optInMutationResponse.fetching || optInMutationResponse.stale;

  const isLoadingPlanId = plansByTierId?.fetching;
  const planId =
    plansByTierId.data?.fetchPlansByTierId?.[0]?.braintreePlanId ?? '';

  const upgradePageTag = getUpgradePageTagForNextTier(sweetpassNextTierName);

  const palette = getSweetpassUpgradeScreenPaletteForTag(upgradePageTag);

  const { content: loadedContent, isLoading: isContentLoading } =
    useUpgradeScreenLoadedContent(
      isSweetpassTierLoading ? undefined : upgradePageTag ?? '',
    );

  const isLoading =
    isLoadingPlanId || isSweetpassTierLoading || isContentLoading;

  const hasContent =
    isSweetpassEnabled && Boolean(loadedContent) && Boolean(palette);

  const isScreenFocused = useIsFocused();
  const shouldRedirectToHome = isScreenFocused && !isLoading && !hasContent;

  // ─────────────────────────────────────────────────────────────────

  useTrackEventEffect({
    name: 'sweetpass_account.review_modal.viewed',
    skip: isLoading || shouldRedirectToHome,
  });

  React.useLayoutEffect(() => {
    if (shouldRedirectToHome) {
      navigation.navigate('MainTabs', {
        screen: 'HomeTab',
        params: { screen: 'Home' },
      });

      return;
    }

    navigation.setOptions({
      headerStyle: {
        backgroundColor:
          palette?.header.backgroundColor ?? theme.colors.KHAKI_3,
      },
      headerTintColor: palette?.header.titleColor,
    });
  }, [navigation, palette, shouldRedirectToHome]);

  const handleCtaConfirmation: SweetpassUpgradeCtaHandler = React.useCallback(
    async (action) => {
      if (action === 'join_sweetpass') {
        track('sweetpass_account.opt_in.tapped');
        const { data } = await optInMutation();

        if (data?.optInFreeTier) {
          track('sweetpass.tier.changed', {
            loyaltyTier: TierName.Sweetpass,
          });
          navigation.navigate('MainTabs', {
            screen: 'LoyaltyTab',
            params: { screen: 'SweetpassHome' },
          });
        }

        return;
      }

      track('sweetpass_account.upgrade_cta.tapped');
      navigation.navigate('Modal', {
        screen: 'SweetpassCheckout',
        params: { planId },
      });
    },
    [navigation, planId, optInMutation, track],
  );

  const handleCta: SweetpassUpgradeCtaHandler = React.useCallback(
    (action) => {
      if (action === 'join_sweetpass') {
        setShowConfirmationModal(true);
      } else {
        handleCtaConfirmation(action);
      }
    },
    [handleCtaConfirmation],
  );

  const handleDismissModal = React.useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return <LoadingAnimation />;
  }

  if (!isSweetpassEnabled || !loadedContent || !palette || !nextTierId) {
    return null;
  }

  return (
    <>
      <SweetpassUpgradeScreenContent
        testID="sweetpass-upgrade-screen"
        content={loadedContent}
        palette={palette}
        ctaHandler={handleCta}
      />
      <SweetpassConfirmationDialog
        visible={showConfirmationModal}
        isLoading={isLoadingOptInFreeTier}
        onDismiss={handleDismissModal}
        onConfirm={() => {
          handleCtaConfirmation(loadedContent.cta.action);
        }}
      />
    </>
  );
};

const getUpgradePageTagForNextTier = (
  nextTierName?: TierName,
): SweetpassUpgradePageTag | undefined => {
  if (nextTierName === TierName.SweetpassPlus) {
    return 'upgrade-to-sweetpass-plus';
  }

  if (nextTierName === TierName.Sweetpass) {
    return 'join-sweetpass';
  }

  return undefined;
};
